<template>
  <div class="page-content">
    <breadcrumb-index
      :items="breadcrumbs"
      icon="layers"
      :badge="totalDecks ? `${totalDecks} decks` : null"
      badge-variant="badge-primary"
    >
      <template #actions>
        <button @click="openCreateDeckModal" class="btn btn-primary btn-sm" :disabled="!selectedExam" v-if="canModify">
          <vue-feather type="plus" size="14" class="me-1" />
          Add Deck
        </button>
      </template>
    </breadcrumb-index>

    <!-- Exam Filter -->
    <div class="filter-cascade-section">
      <div class="filter-cascade-header">
        <vue-feather type="filter" size="18" />
        <h4>Select Exam to View Flashcard Decks</h4>
      </div>
      <div class="filter-cascade-grid">
        <div class="filter-step" :class="{ active: true, completed: selectedExam }">
          <div class="step-number">1</div>
          <div class="step-content">
            <label class="step-label">Select Exam</label>
            <select v-model="selectedExam" class="filter-select" @change="onExamChange" :disabled="loadingExams">
              <option value="">{{ loadingExams ? 'Loading exams...' : 'Choose an exam...' }}</option>
              <option v-for="exam in exams" :key="exam.id" :value="exam.id">{{ exam.name }}</option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <!-- Decks Table -->
    <div v-if="selectedExam" class="decks-content">
      <DataTable
        :items="decks"
        :columns="deckColumns"
        :loading="loadingDecks"
        :paginated="false"
        search-placeholder="Search decks..."
        empty-icon="layers"
        empty-title="No flashcard decks found"
        empty-description="Create your first flashcard deck for this exam."
      >
        <template #filters>
          <select v-model="filterPublished" class="filter-select-modern" @change="currentPage = 1; fetchDecks()">
            <option value="">All Status</option>
            <option value="true">Published</option>
            <option value="false">Unpublished</option>
          </select>
        </template>

        <template #cell-title="{ item }">
          <div class="avatar-info">
            <span class="deck-emoji">{{ item.icon_emoji || '📚' }}</span>
            <div>
              <span class="avatar-name">{{ item.title }}</span>
              <span v-if="item.description" class="avatar-subtitle">{{ item.description }}</span>
            </div>
          </div>
        </template>

        <template #cell-scope="{ item }">
          <div class="scope-tags">
            <span v-if="item.subject_name" class="badge bg-light text-dark">{{ item.subject_name }}</span>
            <span v-if="item.topic_name" class="badge bg-light text-dark">{{ item.topic_name }}</span>
            <span v-if="item.lesson_name" class="badge bg-light text-dark">{{ item.lesson_name }}</span>
            <span v-if="!item.subject_name && !item.topic_name && !item.lesson_name" class="text-muted">—</span>
          </div>
        </template>

        <template #cell-cards="{ item }">
          <span class="badge bg-info">{{ item.card_count }} cards</span>
        </template>

        <template #cell-status="{ item }">
          <span :class="['status-badge', item.is_published ? 'status-active' : 'status-inactive']">
            {{ item.is_published ? 'Published' : 'Draft' }}
          </span>
        </template>

        <template #cell-created="{ item }">
          <span class="text-muted">{{ formatDate(item.created_at) }}</span>
        </template>

        <template #cell-actions="{ item }">
          <div class="action-buttons">
            <button class="action-btn action-view" @click="openCardsPanel(item)" v-tooltip="'Manage cards'">
              <vue-feather type="credit-card" size="14" />
            </button>
            <button v-if="canModify" class="action-btn" @click="togglePublish(item)"
              v-tooltip="item.is_published ? 'Unpublish' : 'Publish'"
              :style="item.is_published ? 'color: #10b981;' : 'color: #9ca3af;'">
              <vue-feather :type="item.is_published ? 'toggle-right' : 'toggle-left'" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-edit" @click="openEditDeckModal(item)" v-tooltip="'Edit deck'">
              <vue-feather type="edit-2" size="14" />
            </button>
            <button v-if="canModify" class="action-btn action-delete" @click="confirmDeleteDeck(item)" v-tooltip="{ text: 'Delete deck', variant: 'danger' }">
              <vue-feather type="trash-2" size="14" />
            </button>
          </div>
        </template>

        <template #empty-action>
          <button @click="openCreateDeckModal" class="btn btn-primary">
            <vue-feather type="plus" size="16" class="me-1" />
            Add First Deck
          </button>
        </template>
      </DataTable>

      <!-- Server-Side Pagination -->
      <div v-if="totalDecks > 0" class="server-pagination">
        <div class="pagination-info">
          Showing {{ startIndex + 1 }}-{{ endIndex }} of {{ totalDecks }}
        </div>
        <div class="pagination-controls">
          <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(1)" title="First page">
            <vue-feather type="chevrons-left" size="16" />
          </button>
          <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)" title="Previous page">
            <vue-feather type="chevron-left" size="16" />
          </button>
          <div class="pagination-pages">
            <button v-for="page in visiblePages" :key="page" class="pagination-page" :class="{ active: page === currentPage, ellipsis: page === '...' }" :disabled="page === '...'" @click="page !== '...' && goToPage(page)">
              {{ page }}
            </button>
          </div>
          <button class="pagination-btn" :disabled="!hasMore" @click="goToPage(currentPage + 1)" title="Next page">
            <vue-feather type="chevron-right" size="16" />
          </button>
          <button class="pagination-btn" :disabled="!hasMore" @click="goToPage(totalPages)" title="Last page">
            <vue-feather type="chevrons-right" size="16" />
          </button>
        </div>
        <div class="pagination-size">
          <select v-model="pageSize" class="page-size-select" @change="onPageSizeChange">
            <option v-for="size in [10, 25, 50, 100]" :key="size" :value="size">{{ size }} per page</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="select-prompt">
      <div class="select-prompt-icon">
        <vue-feather type="layers" size="64" />
      </div>
      <h3>Select an Exam</h3>
      <p>Choose an exam from the filter above to view and manage its flashcard decks.</p>
    </div>

    <!-- Cards Side Panel -->
    <Teleport to="body">
      <Transition name="panel-slide">
        <div v-if="showCardsPanel" class="cards-panel-overlay" @click.self="closeCardsPanel">
          <div class="cards-panel">
            <div class="cards-panel-header">
              <div class="cards-panel-title">
                <span class="deck-emoji-lg">{{ activeDeck?.icon_emoji || '📚' }}</span>
                <div>
                  <h3>{{ activeDeck?.title }}</h3>
                  <span class="text-muted">{{ cards.length }} card(s)</span>
                </div>
              </div>
              <div class="cards-panel-actions">
                <button v-if="canModify" class="btn btn-primary btn-sm" @click="openCreateCardModal">
                  <vue-feather type="plus" size="14" class="me-1" />
                  Add Card
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="closeCardsPanel">
                  <vue-feather type="x" size="14" />
                </button>
              </div>
            </div>

            <div class="cards-panel-body">
              <div v-if="loadingCards" class="cards-loading">
                <vue-feather type="loader" size="24" class="spin" />
                <span>Loading cards...</span>
              </div>

              <div v-else-if="cards.length === 0" class="cards-empty">
                <vue-feather type="credit-card" size="48" />
                <p>No cards in this deck yet.</p>
                <button class="btn btn-primary btn-sm" @click="openCreateCardModal">
                  <vue-feather type="plus" size="14" class="me-1" />
                  Add First Card
                </button>
              </div>

              <div v-else class="cards-list">
                <div v-for="(card, index) in cards" :key="card.id" class="card-item">
                  <div class="card-item-header">
                    <span class="card-number">#{{ index + 1 }}</span>
                    <span :class="['badge', getDifficultyBadge(card.difficulty)]">{{ card.difficulty }}</span>
                    <div class="card-item-actions">
                      <button v-if="canModify" class="action-btn action-edit" @click="openEditCardModal(card)" v-tooltip="'Edit card'">
                        <vue-feather type="edit-2" size="13" />
                      </button>
                      <button v-if="canModify" class="action-btn action-delete" @click="confirmDeleteCard(card)" v-tooltip="{ text: 'Delete', variant: 'danger' }">
                        <vue-feather type="trash-2" size="13" />
                      </button>
                    </div>
                  </div>
                  <div class="card-item-content">
                    <div class="card-side">
                      <label>Front</label>
                      <p>{{ card.front }}</p>
                    </div>
                    <div class="card-side">
                      <label>Back</label>
                      <p>{{ card.back }}</p>
                    </div>
                  </div>
                  <div v-if="card.hint" class="card-hint">
                    <vue-feather type="info" size="12" />
                    {{ card.hint }}
                  </div>
                  <div v-if="card.tags && card.tags.length" class="card-tags">
                    <span v-for="tag in card.tags" :key="tag" class="badge bg-light text-dark">{{ tag }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Create/Edit Deck Modal -->
    <CreateModal
      v-model="showDeckModal"
      :title="isEditingDeck ? 'Edit Deck' : 'Add Deck'"
      icon="layers"
      size="lg"
      :loading="savingDeck"
      :submit-text="isEditingDeck ? 'Update' : 'Create'"
      :submit-disabled="!deckForm.title"
      :item-id="isEditingDeck ? editingDeckId : ''"
      @submit="saveDeck"
    >
      <form @submit.prevent="saveDeck" class="modal-form">
        <div class="form-group">
          <label class="form-label">Title <span class="text-danger">*</span></label>
          <input v-model="deckForm.title" type="text" class="form-control" placeholder="e.g., Algebra Formulas" maxlength="200" required />
          <small class="text-muted">{{ deckForm.title.length }}/200 characters</small>
        </div>

        <div class="form-group">
          <label class="form-label">Description <span class="optional-tag">(Optional)</span></label>
          <textarea v-model="deckForm.description" class="form-control" rows="3" placeholder="Brief description of this deck..." maxlength="500"></textarea>
          <small class="text-muted">{{ (deckForm.description || '').length }}/500 characters</small>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Color</label>
            <div class="color-picker-row">
              <input v-model="deckForm.color" type="color" class="form-control form-control-color" />
              <input v-model="deckForm.color" type="text" class="form-control" placeholder="#6366F1" maxlength="20" />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">Icon Emoji</label>
            <input v-model="deckForm.icon_emoji" type="text" class="form-control" placeholder="📚" maxlength="10" />
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Language</label>
          <select v-model="deckForm.language" class="form-control" @change="onDeckLanguageChange">
            <option v-for="lang in languageOptions" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <small v-if="loadingTranslation" class="text-info"><vue-feather type="loader" size="12" class="spin" /> Loading translation...</small>
        </div>

        <div v-if="!isEditingDeck" class="form-row">
          <div class="form-group">
            <label class="form-label">Subject <span class="optional-tag">(Optional)</span></label>
            <select v-model="deckForm.subject_id" class="form-control" @change="onDeckSubjectChange">
              <option value="">No subject</option>
              <option v-for="s in subjects" :key="s.id" :value="s.id">{{ s.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Topic <span class="optional-tag">(Optional)</span></label>
            <select v-model="deckForm.topic_id" class="form-control" :disabled="!deckForm.subject_id || loadingTopics" @change="onDeckTopicChange">
              <option value="">{{ loadingTopics ? 'Loading...' : 'No topic' }}</option>
              <option v-for="t in topics" :key="t.id" :value="t.id">{{ t.name }}</option>
            </select>
          </div>
        </div>

        <div v-if="!isEditingDeck && deckForm.topic_id" class="form-row">
          <div class="form-group">
            <label class="form-label">Lesson <span class="optional-tag">(Optional)</span></label>
            <select v-model="deckForm.lesson_id" class="form-control" :disabled="!deckForm.topic_id || loadingLessons">
              <option value="">{{ loadingLessons ? 'Loading...' : 'No lesson' }}</option>
              <option v-for="l in lessons" :key="l.id" :value="l.id">{{ l.title || l.name }}</option>
            </select>
            <small class="text-muted">Linking to a lesson makes this deck appear on that specific lesson screen.</small>
          </div>
        </div>
      </form>
    </CreateModal>

    <!-- Create/Edit Card Modal -->
    <CreateModal
      v-model="showCardModal"
      :title="isEditingCard ? 'Edit Card' : 'Add Card'"
      icon="credit-card"
      size="lg"
      :loading="savingCard"
      :submit-text="isEditingCard ? 'Update' : 'Create'"
      :submit-disabled="!cardForm.front || !cardForm.back"
      :item-id="isEditingCard ? editingCardId : ''"
      @submit="saveCard"
    >
      <form @submit.prevent="saveCard" class="modal-form">
        <div class="form-group">
          <label class="form-label">Front <span class="text-danger">*</span></label>
          <textarea v-model="cardForm.front" class="form-control" rows="3" placeholder="Question or prompt..." required></textarea>
        </div>

        <!-- AI Enhance Button -->
        <div class="ai-enhance-section" v-if="cardForm.front">
          <button type="button" @click="aiEnhanceCard" class="ai-enhance-btn" :disabled="aiEnhancing">
            <vue-feather :type="aiEnhancing ? 'loader' : 'cpu'" size="16" :class="{ 'spin': aiEnhancing }" />
            {{ aiEnhancing ? 'Generating...' : 'AI Generate Answer & Hint' }}
          </button>
          <button type="button" @click="aiTranslateCard" class="ai-translate-btn" :disabled="aiTranslating">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'AI Translate' }}
          </button>
          <span class="ai-hint">Auto-fill back, hint & difficulty</span>
        </div>

        <!-- Translation Language Selector -->
        <div class="ai-translate-section" v-if="cardForm.front && showTranslateOptions">
          <label class="form-label">Translate to</label>
          <select v-model="translateTargetLang" class="form-control form-control-sm" style="max-width: 200px;">
            <option v-for="lang in languageOptions.filter(l => l.code !== (deckForm.language || 'en'))" :key="lang.code" :value="lang.code">{{ lang.label }}</option>
          </select>
          <button type="button" @click="confirmTranslateCard" class="ai-translate-btn" :disabled="aiTranslating || !translateTargetLang">
            <vue-feather :type="aiTranslating ? 'loader' : 'globe'" size="16" :class="{ 'spin': aiTranslating }" />
            {{ aiTranslating ? 'Translating...' : 'Translate Now' }}
          </button>
        </div>

        <div class="form-group">
          <label class="form-label">Back <span class="text-danger">*</span></label>
          <textarea v-model="cardForm.back" class="form-control" rows="3" placeholder="Answer or explanation..." required></textarea>
        </div>

        <div class="form-group">
          <label class="form-label">Hint <span class="optional-tag">(Optional)</span></label>
          <input v-model="cardForm.hint" type="text" class="form-control" placeholder="A helpful hint..." maxlength="500" />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Difficulty</label>
            <select v-model="cardForm.difficulty" class="form-control">
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div class="form-group">
            <label class="form-label">Tags <span class="optional-tag">(comma separated)</span></label>
            <input v-model="cardForm.tagsInput" type="text" class="form-control" placeholder="algebra, formulas" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label class="form-label">Front Image URL <span class="optional-tag">(Optional)</span></label>
            <input v-model="cardForm.front_image_url" type="url" class="form-control" placeholder="https://..." />
          </div>
          <div class="form-group">
            <label class="form-label">Back Image URL <span class="optional-tag">(Optional)</span></label>
            <input v-model="cardForm.back_image_url" type="url" class="form-control" placeholder="https://..." />
          </div>
        </div>
      </form>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import DataTable from '@/components/data-table/DataTable.vue'
import CreateModal from '@/components/modals/CreateModal.vue'
import { languageOptions } from '@/utils/languageOptions'

export default {
  name: 'FlashcardsList',
  components: { DataTable, CreateModal },
  setup() {
    const authStore = useAuthStore()
    const { toast, confirmDelete: confirmDeleteAction } = useNotifications()
    const canModify = computed(() => authStore.isSuperAdmin || authStore.isAdmin || authStore.isContentManager)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Flashcards', active: true }
    ]

    const deckColumns = [
      { key: 'title', label: 'Deck', sortable: true, minWidth: '280px' },
      { key: 'scope', label: 'Subject / Topic / Lesson' },
      { key: 'cards', label: 'Cards' },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'created', label: 'Created', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    // Data
    const decks = ref([])
    const loadingDecks = ref(false)
    const totalDecks = ref(0)
    const exams = ref([])
    const subjects = ref([])
    const topics = ref([])
    const lessons = ref([])
    const selectedExam = ref('')
    const loadingExams = ref(false)
    const loadingTopics = ref(false)
    const loadingLessons = ref(false)
    const filterPublished = ref('')

    // Pagination
    const currentPage = ref(1)
    const pageSize = ref(10)
    const hasMore = computed(() => currentPage.value < totalPages.value)
    const totalPages = computed(() => Math.ceil(totalDecks.value / pageSize.value))
    const startIndex = computed(() => (currentPage.value - 1) * pageSize.value)
    const endIndex = computed(() => Math.min(startIndex.value + pageSize.value, totalDecks.value))

    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)
        for (let i = start; i <= end; i++) pages.push(i)
        if (current < total - 2) pages.push('...')
        pages.push(total)
      }
      return pages
    })

    // Deck modal
    const showDeckModal = ref(false)
    const isEditingDeck = ref(false)
    const savingDeck = ref(false)
    const editingDeckId = ref(null)

    const deckForm = reactive({
      title: '',
      description: '',
      color: '#6366F1',
      icon_emoji: '📚',
      subject_id: '',
      topic_id: '',
      lesson_id: '',
      language: 'en'
    })

    // Cards panel
    const showCardsPanel = ref(false)
    const activeDeck = ref(null)
    const cards = ref([])
    const loadingCards = ref(false)

    // Card modal
    const showCardModal = ref(false)
    const isEditingCard = ref(false)
    const savingCard = ref(false)
    const editingCardId = ref(null)
    const aiEnhancing = ref(false)
    const aiTranslating = ref(false)
    const showTranslateOptions = ref(false)
    const translateTargetLang = ref('hi')
    const loadingTranslation = ref(false)

    const cardForm = reactive({
      front: '',
      back: '',
      hint: '',
      difficulty: 'medium',
      tagsInput: '',
      front_image_url: '',
      back_image_url: ''
    })

    // Computed — filtering is handled server-side via fetchDecks params

    // Fetch exams
    const fetchExams = async () => {
      loadingExams.value = true
      try {
        const res = await api.get('/v1/admin/exams')
        exams.value = res.data.exams || []
      } catch (err) {
        console.error('Failed to fetch exams:', err)
        toast.error('Failed to load exams')
      } finally {
        loadingExams.value = false
      }
    }

    // Fetch subjects for selected exam
    const fetchSubjects = async (examId) => {
      if (!examId) { subjects.value = []; return }
      try {
        const res = await api.get(`/v1/admin/exams/${examId}/subjects`)
        subjects.value = res.data.subjects || []
      } catch (err) {
        console.error('Failed to fetch subjects:', err)
        subjects.value = []
      }
    }

    // Fetch topics for selected subject
    const fetchTopics = async (subjectId) => {
      if (!subjectId) { topics.value = []; return }
      loadingTopics.value = true
      try {
        const res = await api.get(`/v1/admin/subjects/${subjectId}/topics`)
        topics.value = res.data.topics || []
      } catch (err) {
        console.error('Failed to fetch topics:', err)
        topics.value = []
      } finally {
        loadingTopics.value = false
      }
    }

    // Fetch lessons for selected topic
    const fetchLessons = async (topicId) => {
      if (!topicId) { lessons.value = []; return }
      loadingLessons.value = true
      try {
        const res = await api.get(`/v1/admin/topics/${topicId}/lessons`)
        lessons.value = res.data.lessons || []
      } catch (err) {
        console.error('Failed to fetch lessons:', err)
        lessons.value = []
      } finally {
        loadingLessons.value = false
      }
    }

    // Fetch decks
    const fetchDecks = async () => {
      if (!selectedExam.value) {
        decks.value = []
        totalDecks.value = 0
        return
      }
      loadingDecks.value = true
      try {
        const params = {
          exam_id: selectedExam.value,
          limit: pageSize.value,
          offset: (currentPage.value - 1) * pageSize.value
        }
        if (filterPublished.value !== '') {
          params.is_published = filterPublished.value
        }
        const res = await api.get('/v1/admin/flashcard-decks', { params })
        const payload = res.data.data || res.data
        decks.value = payload.decks || []
        totalDecks.value = payload.total || decks.value.length
      } catch (err) {
        console.error('Failed to fetch decks:', err)
        toast.error('Failed to load flashcard decks')
        decks.value = []
        totalDecks.value = 0
      } finally {
        loadingDecks.value = false
      }
    }

    // Fetch cards for a deck
    const fetchCards = async (deckId) => {
      loadingCards.value = true
      try {
        const res = await api.get(`/v1/admin/flashcard-decks/${deckId}/cards`)
        const payload = res.data.data || res.data
        cards.value = payload.cards || []
      } catch (err) {
        console.error('Failed to fetch cards:', err)
        toast.error('Failed to load cards')
        cards.value = []
      } finally {
        loadingCards.value = false
      }
    }

    const onExamChange = () => {
      filterPublished.value = ''
      currentPage.value = 1
      subjects.value = []
      topics.value = []
      lessons.value = []
      fetchSubjects(selectedExam.value)
      fetchDecks()
    }

    const goToPage = (page) => {
      currentPage.value = page
      fetchDecks()
    }

    const onPageSizeChange = () => {
      currentPage.value = 1
      fetchDecks()
    }

    const onDeckSubjectChange = () => {
      deckForm.topic_id = ''
      deckForm.lesson_id = ''
      lessons.value = []
      fetchTopics(deckForm.subject_id)
    }

    const onDeckTopicChange = () => {
      deckForm.lesson_id = ''
      fetchLessons(deckForm.topic_id)
    }

    // Deck CRUD
    const resetDeckForm = () => {
      deckForm.title = ''
      deckForm.description = ''
      deckForm.color = '#6366F1'
      deckForm.icon_emoji = '📚'
      deckForm.subject_id = ''
      deckForm.topic_id = ''
      deckForm.lesson_id = ''
      deckForm.language = 'en'
      editingDeckId.value = null
      isEditingDeck.value = false
    }

    const openCreateDeckModal = () => {
      if (!selectedExam.value) return
      resetDeckForm()
      showDeckModal.value = true
    }

    const openEditDeckModal = (deck) => {
      isEditingDeck.value = true
      editingDeckId.value = deck.id
      deckForm.title = deck.title || ''
      deckForm.description = deck.description || ''
      deckForm.color = deck.color || '#6366F1'
      deckForm.icon_emoji = deck.icon_emoji || '📚'
      deckForm.subject_id = deck.subject_id || ''
      deckForm.topic_id = deck.topic_id || ''
      deckForm.language = deck.language || 'en'
      showDeckModal.value = true
    }

    const saveDeck = async () => {
      if (!deckForm.title) return
      savingDeck.value = true
      try {
        const payload = {
          title: deckForm.title,
          description: deckForm.description || null,
          color: deckForm.color,
          icon_emoji: deckForm.icon_emoji,
          language_code: deckForm.language
        }

        if (isEditingDeck.value) {
          await api.put(`/v1/admin/flashcard-decks/${editingDeckId.value}`, payload)
          toast.success('Deck updated successfully')
        } else {
          payload.exam_id = selectedExam.value
          if (deckForm.subject_id) payload.subject_id = deckForm.subject_id
          if (deckForm.topic_id) payload.topic_id = deckForm.topic_id
          if (deckForm.lesson_id) payload.lesson_id = deckForm.lesson_id
          await api.post('/v1/admin/flashcard-decks', payload)
          toast.success('Deck created successfully')
        }
        showDeckModal.value = false
        resetDeckForm()
        fetchDecks()
      } catch (error) {
        toast.error(error.response?.data?.error?.message || 'Failed to save deck')
      } finally {
        savingDeck.value = false
      }
    }

    // Fetch translation when deck language changes in edit mode
    const onDeckLanguageChange = async () => {
      if (!isEditingDeck.value || !editingDeckId.value) return
      loadingTranslation.value = true
      try {
        const res = await api.get('/v1/admin/translations', {
          params: { content_type: 'flashcard_deck', content_id: editingDeckId.value, language_code: deckForm.language }
        })
        const translations = res.data?.translations || []
        if (translations.length > 0) {
          let td = translations[0].translated_data
          if (typeof td === 'string') { try { td = JSON.parse(td) } catch (e) { td = {} } }
          if (td.title) deckForm.title = td.title
          if (td.description !== undefined) deckForm.description = td.description
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
      } finally {
        loadingTranslation.value = false
      }
    }

    const togglePublish = async (deck) => {
      try {
        const action = deck.is_published ? 'unpublish' : 'publish'
        await api.post(`/v1/admin/flashcard-decks/${deck.id}/${action}`)
        const idx = decks.value.findIndex(d => d.id === deck.id)
        if (idx !== -1) {
          decks.value[idx] = { ...decks.value[idx], is_published: !deck.is_published }
        }
        toast.success(`Deck ${deck.is_published ? 'unpublished' : 'published'}`)
      } catch (error) {
        toast.error('Failed to update publish status')
      }
    }

    const confirmDeleteDeck = async (deck) => {
      const confirmed = await confirmDeleteAction(deck.title)
      if (confirmed) {
        try {
          await api.delete(`/v1/admin/flashcard-decks/${deck.id}`)
          decks.value = decks.value.filter(d => d.id !== deck.id)
          totalDecks.value = Math.max(0, totalDecks.value - 1)
          toast.success('Deck deleted')
        } catch (error) {
          toast.error('Failed to delete deck')
        }
      }
    }

    // Cards panel
    const openCardsPanel = (deck) => {
      activeDeck.value = deck
      showCardsPanel.value = true
      fetchCards(deck.id)
    }

    const closeCardsPanel = () => {
      showCardsPanel.value = false
      activeDeck.value = null
      cards.value = []
    }

    // Card CRUD
    const resetCardForm = () => {
      cardForm.front = ''
      cardForm.back = ''
      cardForm.hint = ''
      cardForm.difficulty = 'medium'
      cardForm.tagsInput = ''
      cardForm.front_image_url = ''
      cardForm.back_image_url = ''
      editingCardId.value = null
      isEditingCard.value = false
    }

    const openCreateCardModal = () => {
      resetCardForm()
      showCardModal.value = true
    }

    const openEditCardModal = (card) => {
      isEditingCard.value = true
      editingCardId.value = card.id
      cardForm.front = card.front || ''
      cardForm.back = card.back || ''
      cardForm.hint = card.hint || ''
      cardForm.difficulty = card.difficulty || 'medium'
      cardForm.tagsInput = (card.tags || []).join(', ')
      cardForm.front_image_url = card.front_image_url || ''
      cardForm.back_image_url = card.back_image_url || ''
      showCardModal.value = true
    }

    const saveCard = async () => {
      if (!cardForm.front || !cardForm.back || !activeDeck.value) return
      savingCard.value = true
      try {
        const tags = cardForm.tagsInput
          ? cardForm.tagsInput.split(',').map(t => t.trim()).filter(Boolean)
          : []

        const payload = {
          front: cardForm.front,
          back: cardForm.back,
          hint: cardForm.hint || null,
          difficulty: cardForm.difficulty,
          tags,
          front_image_url: cardForm.front_image_url || null,
          back_image_url: cardForm.back_image_url || null,
          language_code: deckForm.language || 'en'
        }

        if (isEditingCard.value) {
          await api.put(`/v1/admin/flashcard-decks/${activeDeck.value.id}/cards/${editingCardId.value}`, payload)
          toast.success('Card updated')
        } else {
          await api.post(`/v1/admin/flashcard-decks/${activeDeck.value.id}/cards`, payload)
          toast.success('Card created')
        }
        showCardModal.value = false
        resetCardForm()
        fetchCards(activeDeck.value.id)
        // Update card count in deck list
        const idx = decks.value.findIndex(d => d.id === activeDeck.value.id)
        if (idx !== -1 && !isEditingCard.value) {
          decks.value[idx] = { ...decks.value[idx], card_count: decks.value[idx].card_count + 1 }
        }
      } catch (error) {
        toast.error(error.response?.data?.error?.message || 'Failed to save card')
      } finally {
        savingCard.value = false
      }
    }

    const confirmDeleteCard = async (card) => {
      const confirmed = await confirmDeleteAction('this card')
      if (confirmed) {
        try {
          await api.delete(`/v1/admin/flashcard-decks/${activeDeck.value.id}/cards/${card.id}`)
          cards.value = cards.value.filter(c => c.id !== card.id)
          const idx = decks.value.findIndex(d => d.id === activeDeck.value.id)
          if (idx !== -1) {
            decks.value[idx] = { ...decks.value[idx], card_count: Math.max(0, decks.value[idx].card_count - 1) }
          }
          toast.success('Card deleted')
        } catch (error) {
          toast.error('Failed to delete card')
        }
      }
    }

    // AI Enhancement
    const parseAICardResponse = (response) => {
      if (response.data?.structured_data?.data) return response.data.structured_data.data
      const responseText = response.data?.response || response.data?.message || ''
      const jsonMatch = responseText.match(/```(?:json)?\s*([\s\S]*?)```/)
      if (jsonMatch) {
        try { return JSON.parse(jsonMatch[1].trim()) } catch (e) {}
      }
      try {
        const parsed = JSON.parse(responseText)
        return parsed
      } catch (e) {}
      return { back: responseText }
    }

    const aiEnhanceCard = async () => {
      if (!cardForm.front) {
        toast.error('Please enter the front (question) first')
        return
      }
      aiEnhancing.value = true
      try {
        const deckTitle = activeDeck.value?.title || 'General'
        const response = await api.post('/v1/admin/admin-chat', {
          message: `Create a flashcard answer for the following question/prompt.\n\nDeck: "${deckTitle}"\nFront (Question): "${cardForm.front}"\n\nProvide your response as a valid JSON object with these exact keys:\n{\n  "back": "Clear, concise answer or explanation",\n  "hint": "A helpful hint to remember the answer",\n  "difficulty": "easy|medium|hard"\n}\n\nKeep the back answer educational and concise. Do not wrap in markdown.`,
          generation_type: 'flashcard_enhance',
          output_format: 'structured'
        })
        const aiData = parseAICardResponse(response)
        let fieldsUpdated = 0
        if (aiData.back) { cardForm.back = aiData.back; fieldsUpdated++ }
        if (aiData.hint) { cardForm.hint = aiData.hint; fieldsUpdated++ }
        if (aiData.difficulty && ['easy', 'medium', 'hard'].includes(String(aiData.difficulty).toLowerCase())) {
          cardForm.difficulty = String(aiData.difficulty).toLowerCase(); fieldsUpdated++
        }
        if (aiData.tags) {
          const tags = Array.isArray(aiData.tags) ? aiData.tags : String(aiData.tags).split(',').map(t => t.trim())
          cardForm.tagsInput = tags.join(', '); fieldsUpdated++
        }
        if (fieldsUpdated > 0) toast.success(`AI filled ${fieldsUpdated} field(s)`)
        else toast.warning('AI responded but could not extract structured data.')
      } catch (error) {
        console.error('AI enhancement failed:', error)
        toast.error('Failed to enhance card with AI.')
      } finally {
        aiEnhancing.value = false
      }
    }

    // AI Translation
    const aiTranslateCard = () => {
      if (!cardForm.front) {
        toast.error('Please enter the front (question) first')
        return
      }
      showTranslateOptions.value = !showTranslateOptions.value
    }

    const confirmTranslateCard = async () => {
      if (!cardForm.front || !translateTargetLang.value) return
      const targetLangLabel = languageOptions.find(l => l.code === translateTargetLang.value)?.label || translateTargetLang.value
      aiTranslating.value = true
      try {
        const fieldsToTranslate = { front: cardForm.front }
        if (cardForm.back) fieldsToTranslate.back = cardForm.back
        if (cardForm.hint) fieldsToTranslate.hint = cardForm.hint

        const response = await api.post('/v1/admin/admin-chat', {
          message: `Translate the following flashcard content to ${targetLangLabel}. Keep technical terms intact. Return ONLY a valid JSON object with the same keys translated:\n\n${JSON.stringify(fieldsToTranslate, null, 2)}`,
          generation_type: 'translate',
          output_format: 'structured'
        })
        const aiData = parseAICardResponse(response)
        let fieldsUpdated = 0
        if (aiData.front) { cardForm.front = aiData.front; fieldsUpdated++ }
        if (aiData.back) { cardForm.back = aiData.back; fieldsUpdated++ }
        if (aiData.hint) { cardForm.hint = aiData.hint; fieldsUpdated++ }
        if (fieldsUpdated > 0) {
          toast.success(`${fieldsUpdated} field(s) translated to ${targetLangLabel}`)
        } else {
          toast.warning('AI responded but could not extract translated data.')
        }
        showTranslateOptions.value = false
      } catch (error) {
        console.error('AI translation failed:', error)
        toast.error('Failed to translate card with AI.')
      } finally {
        aiTranslating.value = false
      }
    }

    // Helpers
    const getDifficultyBadge = (difficulty) => {
      const badges = { easy: 'bg-success', medium: 'bg-warning text-dark', hard: 'bg-danger' }
      return badges[difficulty] || 'bg-secondary'
    }

    const formatDate = (date) => {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }

    onMounted(fetchExams)

    return {
      breadcrumbs, deckColumns, decks, loadingDecks, totalDecks,
      exams, subjects, topics, lessons, selectedExam, loadingExams, loadingTopics, loadingLessons,
      filterPublished,
      currentPage, pageSize, hasMore, totalPages, startIndex, endIndex, visiblePages,
      showDeckModal, isEditingDeck, savingDeck, editingDeckId, deckForm,
      showCardsPanel, activeDeck, cards, loadingCards,
      showCardModal, isEditingCard, savingCard, editingCardId, cardForm, aiEnhancing,
      canModify,
      onExamChange, onDeckSubjectChange, onDeckTopicChange, fetchDecks,
      goToPage, onPageSizeChange,
      openCreateDeckModal, openEditDeckModal, saveDeck, togglePublish, confirmDeleteDeck,
      openCardsPanel, closeCardsPanel,
      openCreateCardModal, openEditCardModal, saveCard, confirmDeleteCard,
      aiEnhanceCard, aiTranslateCard, confirmTranslateCard,
      aiTranslating, showTranslateOptions, translateTargetLang,
      loadingTranslation, onDeckLanguageChange,
      getDifficultyBadge, formatDate,
      languageOptions
    }
  }
}
</script>

<style scoped>
.filter-cascade-section {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #93c5fd;
}

.filter-cascade-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  color: #2563eb;
}

.filter-cascade-header h4 { margin: 0; font-size: 1rem; font-weight: 600; }

.filter-cascade-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1rem;
}

.filter-step {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  border: 2px solid #e0e7ff;
  transition: all 0.3s ease;
  opacity: 0.6;
}

.filter-step.active { opacity: 1; border-color: #3b82f6; }
.filter-step.completed { border-color: #3b82f6; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); }

.step-number {
  width: 32px; height: 32px; border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white; display: flex; align-items: center; justify-content: center;
  font-weight: 700; font-size: 0.875rem; flex-shrink: 0;
}

.step-content { flex: 1; display: flex; flex-direction: column; gap: 0.5rem; }
.step-label { font-size: 0.75rem; font-weight: 600; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }

.filter-select {
  padding: 0.625rem 0.875rem; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 0.875rem; background: white; cursor: pointer; transition: all 0.2s;
}

.filter-select:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
.filter-select:disabled { background: #f3f4f6; cursor: not-allowed; }

.select-prompt {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 4rem 2rem; text-align: center; background: #f9fafb;
  border-radius: 16px; border: 2px dashed #d1d5db;
}

.select-prompt-icon { color: #9ca3af; margin-bottom: 1.5rem; }
.select-prompt h3 { margin: 0 0 0.5rem; font-size: 1.25rem; color: #374151; }
.select-prompt p { margin: 0; color: #6b7280; max-width: 400px; }

.decks-content { animation: fadeIn 0.3s ease; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

.deck-emoji { font-size: 1.5rem; margin-right: 0.75rem; }
.deck-emoji-lg { font-size: 2rem; }

.avatar-info { display: flex; align-items: center; }
.avatar-info > div { display: flex; flex-direction: column; }
.avatar-name { font-weight: 600; color: #1f2937; }
.avatar-subtitle { font-size: 0.8rem; color: #6b7280; margin-top: 2px; }

.scope-tags { display: flex; gap: 0.25rem; flex-wrap: wrap; }

/* Cards Panel */
.cards-panel-overlay {
  position: fixed; inset: 0; z-index: 1500;
  background: rgba(0, 0, 0, 0.5); backdrop-filter: blur(4px);
  display: flex; justify-content: flex-end;
}

.cards-panel {
  width: 600px; max-width: 90vw; height: 100vh;
  background: white; display: flex; flex-direction: column;
  box-shadow: -10px 0 40px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease;
}

@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }

.cards-panel-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1.25rem 1.5rem; border-bottom: 1px solid #e5e7eb; flex-shrink: 0;
}

.cards-panel-title { display: flex; align-items: center; gap: 0.75rem; }
.cards-panel-title h3 { margin: 0; font-size: 1.1rem; font-weight: 600; }
.cards-panel-actions { display: flex; gap: 0.5rem; }

.cards-panel-body { flex: 1; overflow-y: auto; padding: 1.5rem; }

.cards-loading, .cards-empty {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 3rem 1rem; color: #9ca3af; gap: 0.75rem; text-align: center;
}

.cards-list { display: flex; flex-direction: column; gap: 1rem; }

.card-item {
  border: 1px solid #e5e7eb; border-radius: 12px; padding: 1rem;
  transition: box-shadow 0.2s;
}

.card-item:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); }

.card-item-header {
  display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;
}

.card-number { font-weight: 700; color: #6b7280; font-size: 0.8rem; }
.card-item-actions { margin-left: auto; display: flex; gap: 0.25rem; }

.card-item-content { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

.card-side label {
  font-size: 0.7rem; font-weight: 600; text-transform: uppercase;
  color: #9ca3af; letter-spacing: 0.5px; margin-bottom: 0.25rem; display: block;
}

.card-side p { margin: 0; font-size: 0.875rem; color: #374151; line-height: 1.5; }

.card-hint {
  display: flex; align-items: center; gap: 0.5rem;
  margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid #f3f4f6;
  font-size: 0.8rem; color: #6b7280; font-style: italic;
}

.card-tags { display: flex; gap: 0.25rem; flex-wrap: wrap; margin-top: 0.5rem; }

/* Modal form */
.modal-form { display: flex; flex-direction: column; gap: 1rem; }
.form-group { display: flex; flex-direction: column; gap: 0.5rem; }
.form-label { font-weight: 500; font-size: 0.875rem; color: #374151; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.optional-tag { font-weight: 400; color: #94a3b8; }

.form-control {
  padding: 0.625rem 0.875rem; border: 1px solid #d1d5db; border-radius: 8px;
  font-size: 0.875rem; transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

.form-control-color { width: 48px; height: 38px; padding: 4px; cursor: pointer; }

.color-picker-row { display: flex; gap: 0.5rem; align-items: center; }

/* Panel transition */
.panel-slide-enter-active { transition: opacity 0.3s ease; }
.panel-slide-enter-active .cards-panel { transition: transform 0.3s ease; }
.panel-slide-leave-active { transition: opacity 0.2s ease; }
.panel-slide-leave-active .cards-panel { transition: transform 0.2s ease; }
.panel-slide-enter-from { opacity: 0; }
.panel-slide-enter-from .cards-panel { transform: translateX(100%); }
.panel-slide-leave-to { opacity: 0; }
.panel-slide-leave-to .cards-panel { transform: translateX(100%); }

.spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* AI Enhance Section */
.ai-enhance-section {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  border: 1px solid #fbbf24;
  flex-wrap: wrap;
}

.ai-enhance-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-enhance-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.ai-enhance-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.ai-translate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.4);
}

.ai-translate-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.ai-translate-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border-radius: 10px;
  border: 1px solid #7dd3fc;
}

.ai-hint {
  font-size: 0.8rem;
  color: #92400e;
}

/* Dark mode */
:global(body.dark-mode) .filter-cascade-section { background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%); border-color: #334155; }
:global(body.dark-mode) .filter-cascade-header { color: #60a5fa; }
:global(body.dark-mode) .filter-step { background: #1e293b; border-color: #334155; }
:global(body.dark-mode) .filter-step.completed { background: linear-gradient(135deg, #1e293b 0%, #172554 100%); }
:global(body.dark-mode) .filter-select { background: #1e293b; border-color: #374151; color: #f3f4f6; }
:global(body.dark-mode) .select-prompt { background: #111827; border-color: #374151; }
:global(body.dark-mode) .select-prompt h3 { color: #f3f4f6; }
:global(body.dark-mode) .select-prompt p { color: #9ca3af; }
:global(body.dark-mode) .cards-panel { background: #1f2937; }
:global(body.dark-mode) .cards-panel-header { border-color: #374151; }
:global(body.dark-mode) .card-item { border-color: #374151; }
:global(body.dark-mode) .card-item:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3); }
:global(body.dark-mode) .card-side p { color: #e5e7eb; }
:global(body.dark-mode) .card-hint { border-color: #374151; color: #9ca3af; }
:global(body.dark-mode) .avatar-name { color: #f3f4f6; }
:global(body.dark-mode) .form-label { color: #d1d5db; }
:global(body.dark-mode) .form-control { background: #1e293b; border-color: #374151; color: #f3f4f6; }
:global(body.dark-mode) .ai-enhance-section { background: linear-gradient(135deg, #78350f 0%, #92400e 100%); border-color: #b45309; }
:global(body.dark-mode) .ai-translate-section { background: linear-gradient(135deg, #0c4a6e 0%, #075985 100%); border-color: #0369a1; }
:global(body.dark-mode) .ai-hint { color: #fbbf24; }

/* Server-Side Pagination */
.server-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  margin-top: 1rem;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
}

.pagination-btn:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-pages {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 4px;
}

.pagination-page {
  min-width: 36px;
  height: 36px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-page:hover:not(:disabled):not(.active) {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.pagination-page.active {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-color: #6366f1;
  color: white;
}

.pagination-page.ellipsis {
  border: none;
  background: transparent;
  cursor: default;
}

.pagination-size {
  display: flex;
  align-items: center;
}

.page-size-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  color: #374151;
  cursor: pointer;
}

.page-size-select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

:global(body.dark-mode) .server-pagination {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .pagination-info {
  color: #9ca3af;
}

:global(body.dark-mode) .pagination-btn {
  background: #1e293b;
  border-color: #374151;
  color: #d1d5db;
}

:global(body.dark-mode) .pagination-btn:hover:not(:disabled) {
  background: #334155;
}

:global(body.dark-mode) .pagination-page {
  background: #1e293b;
  border-color: #374151;
  color: #d1d5db;
}

:global(body.dark-mode) .pagination-page:hover:not(:disabled):not(.active) {
  background: #334155;
}

:global(body.dark-mode) .page-size-select {
  background: #1e293b;
  border-color: #374151;
  color: #d1d5db;
}
</style>
